import React, { useEffect, useState, useMemo, useRef } from 'react'
import { supabase } from '../../supabaseClient'
import Header from '../../components/Header'
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid,
    Tooltip, ResponsiveContainer, BarChart, Bar, Cell
} from 'recharts'

if (typeof document !== 'undefined' && !document.getElementById('shimmer-keyframes')) {
    const style = document.createElement('style')
    style.id = 'shimmer-keyframes'
    style.innerHTML = `
        @keyframes shimmer {
            0% { background-position: -400px 0; }
            100% { background-position: 400px 0; }
        }
    `
    document.head.appendChild(style)
}

const RANK_COLORS = ['#A0826D', '#B8957E', '#C9A98F', '#D9BCA3', '#E8D0BC']
const BAR_COLORS = ['#A0826D', '#B8957E', '#C9A98F', '#D9BCA3', '#E8D0BC', '#EDD9C8', '#F2E4D8', '#F6EDE5', '#F9F3EE', '#FBF7F4']

export const formatCurrency = (v) => `₱${parseFloat(v || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
export const formatShortCurrency = (v) => v >= 1000 ? `₱${(v / 1000).toFixed(1)}k` : `₱${Math.round(v)}`
export const formatTimeAgo = (dateString) => {
    const diff = Math.floor((new Date() - new Date(dateString)) / 60000)
    if (diff < 1) return 'Just now'
    if (diff < 60) return `${diff} min ago`
    const h = Math.floor(diff / 60)
    if (h < 24) return `${h} hour${h > 1 ? 's' : ''} ago`
    const d = Math.floor(h / 24)
    return `${d} day${d > 1 ? 's' : ''} ago`
}
export const getStatusColor = (status) => {
    switch (status) {
        case 'pending': return 'bg-yellow-100 text-yellow-800'
        case 'preparing': return 'bg-blue-100 text-blue-800'
        case 'ready': return 'bg-purple-100 text-purple-800'
        case 'completed': return 'bg-green-100 text-green-800'
        case 'cancelled': return 'bg-red-100 text-red-800'
        default: return 'bg-gray-100 text-gray-800'
    }
}

export const getBranchStatusColor = (status) => {
    switch (status) {
        case 'operating': return 'bg-green-100 text-green-700'
        case 'closed': return 'bg-red-100 text-red-700'
        case 'maintenance': return 'bg-amber-100 text-amber-700'
        case 'inactive': return 'bg-gray-200 text-gray-600'
        default: return 'bg-gray-100 text-gray-600'
    }
}

export const stripBranchPrefix = (name = '') => {
    const stripped = name.replace(/^coffee\s*gus[·]?to\s*[-–:]?\s*/i, '').trim()
    return stripped || name
}

export const PAYMENT_LABELS = {
    cash: 'Cash',
    gcash: 'GCash',
    card: 'Card',
    credit_card: 'Credit Card',
    debit_card: 'Debit Card',
    paymongo: 'PayMongo',
    xendit: 'Xendit',
    bank_transfer: 'Bank Transfer',
    e_wallet: 'E-Wallet',
    marketing_pullout: 'Marketing Pullout',
}
export const formatPaymentLabel = (m) => (PAYMENT_LABELS[m] || (m || 'Unknown')
    .toString()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase()))

// Case-insensitive, symbol-stripped grouping key so "GCash", "gcash!", "G-Cash" all collapse together
export const normalizePaymentMethod = (m) => {
    const cleaned = String(m || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
    return cleaned || 'unknown'
}

export const SkeletonBlock = ({ className = '', style = {} }) => (
    <div
        className={`rounded ${className}`}
        style={{
            backgroundImage: 'linear-gradient(90deg, #e5e7eb 0px, #f3f4f6 40px, #e5e7eb 80px)',
            backgroundSize: '400px 100%',
            animation: 'shimmer 1.4s ease-in-out infinite',
            ...style,
        }}
    />
)
export const PRESETS = [
    { label: 'Today', value: '1d' },
    { label: '7 days', value: '7d' },
    { label: '30 days', value: '30d' },
    { label: 'Custom', value: 'custom' },
]

export const presetStart = (preset) => {
    const days = { '1d': 1, '7d': 7, '30d': 30 }[preset] ?? 30
    return new Date(Date.now() - days * 86400000)
}



export const DateRangeBar = ({ preset, setPreset, customStart, setCustomStart, customEnd, setCustomEnd }) => {
    const hasCustom = customStart && customEnd
    const isCustomActive = preset === 'custom'

    const handlePreset = (v) => {
        setPreset(v)
        if (v !== 'custom') { setCustomStart(''); setCustomEnd('') }
    }

    return (
        <div className="flex flex-col gap-2 items-end">
            <div className="flex gap-1.5 flex-wrap justify-end">
                {PRESETS.map(o => (
                    <button
                        key={o.value}
                        onClick={() => handlePreset(o.value)}
                        className="px-3 py-1 rounded-full text-xs border transition-colors"
                        style={{
                            background: preset === o.value ? '#A0826D' : '#fff',
                            borderColor: preset === o.value ? '#A0826D' : '#E5E7EB',
                            color: preset === o.value ? '#fff' : '#6B7280',
                            cursor: 'pointer',
                        }}
                    >
                        {o.label}
                    </button>
                ))}
            </div>

            {isCustomActive && (
                <div className="flex items-center gap-2 flex-wrap justify-end pt-1.5 pb-1 border-t border-dashed border-gray-200">
                    <span className="text-xs text-gray-400">From:</span>
                    <input
                        type="date"
                        value={customStart}
                        onChange={e => setCustomStart(e.target.value)}
                        className="rounded-lg border px-2 py-1 text-xs outline-none"
                        style={{ borderColor: hasCustom ? '#A0826D' : '#E5E7EB', color: '#111827', background: '#fff' }}
                    />
                    <span className="text-xs text-gray-400">To:</span>
                    <input
                        type="date"
                        value={customEnd}
                        max={new Date().toISOString().split('T')[0]}
                        onChange={e => setCustomEnd(e.target.value)}
                        className="rounded-lg border px-2 py-1 text-xs outline-none"
                        style={{ borderColor: hasCustom ? '#A0826D' : '#E5E7EB', color: '#111827', background: '#fff' }}
                    />
                    {hasCustom && (
                        <button
                            onClick={() => { setCustomStart(''); setCustomEnd(''); setPreset('30d') }}
                            className="text-xs px-2 py-1 rounded-full border border-gray-200 text-gray-400 bg-white"
                            style={{ cursor: 'pointer' }}
                        >
                            ✕ Clear
                        </button>
                    )}
                </div>
            )}
        </div>
    )
}

export function SalesTooltip({ active, payload, label }) {
    if (!active || !payload?.length) return null
    return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm">
            <p className="font-semibold text-gray-700 mb-1">{label}</p>
            {payload.map((entry, i) => (
                <p key={i} style={{ color: entry.color }}>
                    {entry.name === 'sales'
                        ? `Sales: ${formatCurrency(entry.value)}`
                        : `Orders: ${entry.value}`}
                </p>
            ))}
        </div>
    )
}

export function TopSellingItems({ allOrders = [], allOrderItems = [], branches = [], compact = false, dateStart, dateEnd, refreshing = false }) {
    const [rankBy, setRankBy] = useState('quantity')

    const items = useMemo(() => {
        const eligibleOrderIds = new Set(
            allOrders
                .filter(o => {
                    if (o.order_status !== 'completed') return false
                    const d = new Date(o.created_at)
                    if (dateStart && d < dateStart) return false
                    if (dateEnd && d > dateEnd) return false
                    return true
                })
                .map(o => o.order_id)
        )
        if (eligibleOrderIds.size === 0) return []

        const agg = {}
        allOrderItems.forEach(row => {
            if (!eligibleOrderIds.has(row.order_id)) return
            const item = row.menu_items || null
            const itemId = item?.item_id || row.item_id
            const itemName = item?.name || row.name || 'Unknown Item'
            const itemCat = item?.category || row.category || ''
            const itemImg = item?.item_img || row.item_img || null
            const unitPrice = parseFloat(row.item_price ?? row.unit_price ?? item?.item_price ?? 0)
            if (!itemId) return
            if (!agg[itemId]) agg[itemId] = { item_id: itemId, name: itemName, category: itemCat, item_img: itemImg, quantity: 0, revenue: 0 }
            agg[itemId].quantity += Number(row.quantity) || 0
            agg[itemId].revenue += unitPrice * (Number(row.quantity) || 0)
        })

        return Object.values(agg).sort((a, b) => b[rankBy] - a[rankBy]).slice(0, 5)
    }, [allOrders, allOrderItems, dateStart, dateEnd, rankBy])

    const maxValue = useMemo(() => items.length ? Math.max(...items.map(i => i[rankBy])) : 1, [items, rankBy])

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 flex flex-col h-full">
            <div className="px-5 py-4 border-b border-gray-100">
                <div className="flex items-center justify-between mb-3">
                    <div>
                        <h2 className="text-base font-bold text-gray-800">Top Selling Items</h2>
                        <p className="text-xs text-gray-500 mt-0.5">Based on selected period</p>
                    </div>
                </div>
                <div className="flex items-center bg-gray-100 rounded-lg p-0.5 gap-0.5 w-fit">
                    {[['quantity', 'By Qty'], ['revenue', 'By Rev']].map(([key, label]) => (
                        <button
                            key={key}
                            onClick={() => setRankBy(key)}
                            className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${rankBy === key ? 'bg-[#A0826D] text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="p-4 flex-1 overflow-y-auto">
                {refreshing ? (
                    <div className="space-y-2">
                        {Array.from({ length: 5 }).map((_, i) => (
                            <div key={i} className="flex items-center gap-3 p-2.5">
                                <div className="w-7 h-7 rounded-full bg-gray-200 animate-pulse flex-shrink-0" />
                                <div className="w-8 h-8 rounded-lg bg-gray-200 animate-pulse flex-shrink-0" />
                                <div className="flex-1 space-y-1.5">
                                    <div className="h-3 bg-gray-200 rounded animate-pulse w-3/4" />
                                    <div className="h-1 bg-gray-100 rounded-full" />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : items.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                        <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" className="mb-2 text-gray-300">
                            <path fill="currentColor" d="M18.5 3H6v10a4 4 0 0 0 4 4h2v2H8v2h8v-2h-4v-2h2a4 4 0 0 0 4-4v-1h.5A3.5 3.5 0 0 0 22 8.5A3.5 3.5 0 0 0 18.5 5zm0 7H18V7h.5A1.5 1.5 0 0 1 20 8.5A1.5 1.5 0 0 1 18.5 10" />
                        </svg>
                        <p className="text-xs font-medium">No sales data found</p>
                        <p className="text-xs mt-0.5">Try a different period</p>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {items.map((item, index) => {
                            const barPercent = (item[rankBy] / maxValue) * 100
                            return (
                                <div key={item.item_id}
                                    className={`flex items-center gap-3 p-2.5 rounded-xl transition-colors hover:bg-gray-50 ${index === 0 ? 'bg-orange-50 hover:bg-orange-100' : ''}`}
                                >
                                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${index === 0 ? 'bg-[#A0826D] text-white' :
                                            index === 1 ? 'bg-[#C9A98F] text-white' :
                                                index === 2 ? 'bg-[#D9BCA3] text-white' :
                                                    'bg-gray-100 text-gray-500'
                                        }`}>
                                        {index + 1}
                                    </div>

                                    {item.item_img ? (
                                        <img src={item.item_img} alt={item.name} className="w-8 h-8 rounded-lg object-cover border border-gray-100 flex-shrink-0" />
                                    ) : (
                                        <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" className="text-gray-300">
                                                <path fill="currentColor" d="M18.5 3H6v10a4 4 0 0 0 4 4h2v2H8v2h8v-2h-4v-2h2a4 4 0 0 0 4-4v-1h.5A3.5 3.5 0 0 0 22 8.5A3.5 3.5 0 0 0 18.5 5zm0 7H18V7h.5A1.5 1.5 0 0 1 20 8.5A1.5 1.5 0 0 1 18.5 10" />
                                            </svg>
                                        </div>
                                    )}

                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between mb-1">
                                            <p className={`text-xs font-semibold truncate ${index === 0 ? 'text-[#7a5c4a]' : 'text-gray-800'}`}>
                                                {item.name}
                                            </p>
                                            <div className="flex flex-col items-end ml-2 flex-shrink-0">
                                                <p className="text-xs font-bold text-gray-800">
                                                    {rankBy === 'revenue' ? formatShortCurrency(item.revenue) : `${item.quantity} sold`}
                                                </p>
                                                <p className="text-[10px] text-gray-400">
                                                    {rankBy === 'revenue' ? `${item.quantity} sold` : formatShortCurrency(item.revenue)}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
                                            <div className="h-full rounded-full transition-all duration-500"
                                                style={{ width: `${barPercent}%`, background: RANK_COLORS[Math.min(index, RANK_COLORS.length - 1)] }}
                                            />
                                        </div>
                                    </div>

                                    {index === 0 && (
                                        <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 24 24" className="text-[#EFBF04] flex-shrink-0">
                                            <path fill="currentColor" d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11zm14 3a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-1h14z" />
                                        </svg>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                )}
            </div>

            {items.length > 0 && (
                <div className="px-5 py-3 border-t border-gray-50 flex items-center justify-between text-sm text-gray-600">
                    <span>Top {items.length} items</span>
                    <span>
                        {rankBy === 'revenue'
                            ? formatShortCurrency(items.reduce((s, i) => s + i.revenue, 0))
                            : `${items.reduce((s, i) => s + i.quantity, 0)} units`} total
                    </span>
                </div>
            )}
        </div>
    )
}

const METHOD_COLORS = ['#A0826D', '#3B82F6', '#16A34A', '#7C3AED', '#D97706', '#DC2626', '#0D9488']

export function PaymentMethodsBreakdown({ methods = [], top, totalAmount, totalCount, refreshing = false, onViewAnalytics }) {
    const [currentPage, setCurrentPage] = useState(1)
    const methodsPerPage = 5
    const totalPages = Math.max(1, Math.ceil(methods.length / methodsPerPage))
    const paginatedMethods = methods.slice((currentPage - 1) * methodsPerPage, currentPage * methodsPerPage)

    useEffect(() => {
        setCurrentPage(1)
    }, [methods.length])

    return (
        <div
            className={`bg-white rounded-xl shadow-sm border border-gray-100 ${onViewAnalytics ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
            onClick={onViewAnalytics}
        >
            <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
                <div>
                    <h2 className="text-lg font-bold text-gray-800">Payment Methods</h2>
                    <p className="text-sm text-gray-500 mt-0.5">Most used payment method in selected period</p>
                </div>
                {!refreshing && totalCount > 0 && (
                    <span className="text-xs text-[#A0826D] font-medium bg-orange-50 px-3 py-1 rounded-full">
                        {totalCount} transaction{totalCount !== 1 ? 's' : ''}
                    </span>
                )}
            </div>

            {!refreshing && totalCount > 0 && (
                <div className="px-6 py-3 border-b border-gray-50 flex items-center justify-between">
                    <span className="text-sm text-gray-500">Total collected</span>
                    <span className="text-xl font-bold text-gray-800">{formatCurrency(totalAmount)}</span>
                </div>
            )}

            <div className="px-6 pb-6 pt-4">
                {refreshing ? (
                    <div className="space-y-3">
                        <SkeletonBlock className="w-full h-20" />
                        <SkeletonBlock className="w-full h-10" />
                        <SkeletonBlock className="w-full h-10" />
                    </div>
                ) : !top ? (
                    <div className="flex flex-col items-center justify-center py-10 text-gray-400">
                        <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" className="mb-2 text-gray-300">
                            <path fill="currentColor" d="M20 4H4c-1.11 0-2 .89-2 2v12c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2m0 14H4v-6h16zm0-10H4V6h16z" />
                        </svg>
                        <p className="text-xs font-medium">No payment data found</p>
                        <p className="text-xs mt-0.5">Try a different period</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Highlighted top method */}
                        <div className="rounded-xl border p-5 flex flex-col justify-between"
                            style={{ borderColor: '#A0826D33', background: '#A0826D08' }}>
                            <div className="flex items-center justify-between mb-4">
                                <span className="text-xs font-semibold uppercase tracking-wider text-[#A0826D]">
                                    Most Used
                                </span>
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" className="text-[#A0826D]">
                                    <path fill="currentColor" d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11zm14 3a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-1h14z" />
                                </svg>
                            </div>
                            <p className="text-2xl font-bold text-gray-800 mb-1">{formatPaymentLabel(top.method)}</p>
                            <p className="text-sm text-gray-500 mb-4">
                                {top.count} transaction{top.count !== 1 ? 's' : ''}
                                {' · '}
                                {formatCurrency(top.amount)}
                            </p>
                            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div className="h-full rounded-full" style={{
                                    width: `${totalCount ? (top.count / totalCount) * 100 : 0}%`,
                                    background: '#A0826D',
                                }} />
                            </div>
                            <p className="text-xs text-gray-400 mt-1.5">
                                {totalCount ? ((top.count / totalCount) * 100).toFixed(1) : '0'}% of all transactions
                            </p>
                        </div>

                        {/* Breakdown list */}
                        <div className="space-y-2.5">
                            {paginatedMethods.map((m, i) => {
                                const pct = totalCount ? (m.count / totalCount) * 100 : 0
                                const rank = (currentPage - 1) * methodsPerPage + i + 1
                                return (
                                    <div key={m.method} className="flex items-center gap-3">
                                        <span className="text-xs font-semibold text-gray-400 w-4 flex-shrink-0">{rank}</span>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="text-xs font-semibold text-gray-700 truncate">
                                                    {formatPaymentLabel(m.method)}
                                                </span>
                                                <span className="text-xs text-gray-500 flex-shrink-0 ml-2">
                                                    {m.count} · {formatShortCurrency(m.amount)}
                                                </span>
                                            </div>
                                            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                                <div className="h-full rounded-full transition-all duration-500"
                                                    style={{ width: `${pct}%`, background: METHOD_COLORS[i % METHOD_COLORS.length] }} />
                                            </div>
                                        </div>
                                        <span className="text-xs text-gray-400 w-10 text-right flex-shrink-0">{pct.toFixed(0)}%</span>
                                    </div>
                                )
                            })}

                            {totalPages > 1 && (
                                <div className="flex items-center justify-between pt-2 mt-1 border-t border-gray-50">
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setCurrentPage(p => Math.max(1, p - 1)) }}
                                        disabled={currentPage === 1}
                                        className="text-xs px-2 py-1 rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                                    >
                                        ← Prev
                                    </button>
                                    <span className="text-xs text-gray-400">
                                        Page {currentPage} of {totalPages}
                                    </span>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); setCurrentPage(p => Math.min(totalPages, p + 1)) }}
                                        disabled={currentPage === totalPages}
                                        className="text-xs px-2 py-1 rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                                    >
                                        Next →
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {!refreshing && totalCount > 0 && (
                <div className="px-6 py-3 border-t border-gray-50 text-sm text-gray-600">
                    <span>{methods.length} payment method{methods.length !== 1 ? 's' : ''}</span>
                </div>
            )}
        </div>
    )
}

export default function DashboardOverview({ onGoToProfile, setActiveMenu }) {
    const [stats, setStats] = useState({ todaySales: 0, salesGrowth: 0, totalOrders: 0, ordersGrowth: 0, activeCustomers: 0, customersGrowth: 0 })
    const [recentOrders, setRecentOrders] = useState([])
    // const [loading, setLoading] = useState(true)
    const [refreshing, setRefreshing] = useState(false)
    const [branches, setBranches] = useState([])
    const [selectedBranch, setSelectedBranch] = useState('all')
    const [branchDropdownOpen, setBranchDropdownOpen] = useState(false)
    const branchDropdownRef = useRef(null)
    const [allOrders, setAllOrders] = useState([])
    const [allOrderItems, setAllOrderItems] = useState([])
    const [transactions, setTransactions] = useState([])

    const [preset, setPreset] = useState('30d')
    const [customStart, setCustomStart] = useState('')
    const [customEnd, setCustomEnd] = useState('')
    const [appliedCustomStart, setAppliedCustomStart] = useState('')
    const [appliedCustomEnd, setAppliedCustomEnd] = useState('')

    const hasCustom = appliedCustomStart && appliedCustomEnd

    const dateStart = useMemo(() => {
        if (hasCustom) { const d = new Date(appliedCustomStart); d.setHours(0, 0, 0, 0); return d }
        return presetStart(preset)
    }, [hasCustom, appliedCustomStart, preset])

    const dateEnd = useMemo(() => {
        if (hasCustom) { const d = new Date(appliedCustomEnd); d.setHours(23, 59, 59, 999); return d }
        return new Date()
    }, [hasCustom, appliedCustomEnd])

    const [chartPeriod, setChartPeriod] = useState('day')

    const goToFilteredMenu = (menuName) => {
        try {
            const toDateStr = (d) => d.toISOString().split('T')[0]
            sessionStorage.setItem('dashboardFilterHandoff', JSON.stringify({
                branchId: selectedBranch !== 'all' ? selectedBranch : null,
                preset: hasCustom ? 'custom' : preset,
                customStart: toDateStr(dateStart),
                customEnd: toDateStr(dateEnd),
            }))
        } catch (err) {
            console.error('Failed to store dashboard filter handoff:', err)
        }
        setActiveMenu(menuName)
    }

    useEffect(() => {
        supabase.auth.getSession().then(({ data: { session } }) => {
            console.log('role:', session ? 'authenticated' : 'anon', session)
        })
        fetchBranches()
    }, [])

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (branchDropdownRef.current && !branchDropdownRef.current.contains(e.target)) {
                setBranchDropdownOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    useEffect(() => {
        if (preset !== 'custom') {
            setAppliedCustomStart('')
            setAppliedCustomEnd('')
        }
    }, [preset])

    useEffect(() => {
        const isValid = (s) => s?.length === 10 && !isNaN(new Date(s))
        if (isValid(customStart) && isValid(customEnd)) {
            setAppliedCustomStart(customStart)
            setAppliedCustomEnd(customEnd)
        }
    }, [customStart, customEnd])

    useEffect(() => {
        fetchDashboardData()

        const ordersChannel = supabase
            .channel('dashboard-orders-channel')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'orders' }, () => fetchDashboardData())
            .subscribe()
        const customersChannel = supabase
            .channel('dashboard-customers-channel')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'customers' }, () => fetchDashboardData())
            .subscribe()
        return () => {
            supabase.removeChannel(ordersChannel)
            supabase.removeChannel(customersChannel)
        }
    }, [selectedBranch, dateStart, dateEnd])

    const fetchBranches = async () => {
        try {
            const { data, error } = await supabase.from('branches').select('branch_id, name, status').order('name')
            if (error) throw error
            setBranches(data || [])
        } catch (err) { console.error('Error fetching branches:', err) }
    }

    const fetchDashboardData = async () => {
        try {
            // setLoading(true)
            setRefreshing(true)
            let ordersQuery = supabase
                .from('orders')
                .select('*')
                .gte('created_at', dateStart.toISOString())
                .lte('created_at', dateEnd.toISOString())
                .order('created_at', { ascending: false })
            if (selectedBranch !== 'all') ordersQuery = ordersQuery.eq('branch_id', selectedBranch)

            const { data: ordersData, error: ordersError } = await ordersQuery
            if (ordersError) throw ordersError
            setAllOrders(ordersData || [])

            const allOrderIds = (ordersData || []).map(o => o.order_id)
            if (allOrderIds.length > 0) {
                const { data: txData, error: txError } = await supabase
                    .from('order_transactions')
                    .select('order_id, payment_method, transaction_amount, status, created_at')
                    .in('order_id', allOrderIds)
                if (txError) throw txError
                setTransactions(txData || [])
            } else {
                setTransactions([])
            }

            const completedOrderIds = (ordersData || [])
                .filter(o => o.order_status === 'completed')
                .map(o => o.order_id)

            if (completedOrderIds.length > 0) {
                const { data: itemsData, error: itemsError } = await supabase
                    .from('order_items')
                    .select(`order_id, item_id, quantity, item_price, menu_items(item_id, name, category, item_img, item_price)`)
                    .in('order_id', completedOrderIds)
                if (itemsError) throw itemsError
                setAllOrderItems(itemsData || [])
            } else {
                setAllOrderItems([])
            }

            const { data: allCustomers, error: customersError } = await supabase.from('customers').select('*')
            if (customersError) throw customersError

            const completedOrders = (ordersData || []).filter(o => o.order_status === 'completed')
            const todaySales = completedOrders.reduce((s, o) => s + parseFloat(o.final_amount || 0), 0)
            const totalOrdersCount = completedOrders.length

            const rangeLengthMs = dateEnd - dateStart
            const prevStart = new Date(dateStart.getTime() - rangeLengthMs)
            const prevEnd = new Date(dateStart)

            let prevQuery = supabase
                .from('orders')
                .select('final_amount, order_status')
                .gte('created_at', prevStart.toISOString())
                .lte('created_at', prevEnd.toISOString())
            if (selectedBranch !== 'all') prevQuery = prevQuery.eq('branch_id', selectedBranch)
            const { data: prevOrders } = await prevQuery

            const prevSales = (prevOrders || []).filter(o => o.order_status === 'completed').reduce((s, o) => s + parseFloat(o.final_amount || 0), 0)
            const prevOrdersCount = (prevOrders || []).length
            const salesGrowth = prevSales > 0 ? ((todaySales - prevSales) / prevSales * 100) : 0
            const ordersGrowth = prevOrdersCount > 0 ? ((totalOrdersCount - prevOrdersCount) / prevOrdersCount * 100) : 0

            const activeCustomers = allCustomers.filter(c =>
                new Date(c.created_at) >= dateStart &&
                new Date(c.created_at) <= dateEnd &&
                c.account_status === 'active'
            ).length
            const recentCustomers = allCustomers.filter(c => new Date(c.created_at) >= dateStart && c.account_status === 'active').length
            const prevCustomers = allCustomers.filter(c => {
                const d = new Date(c.created_at)
                return d >= prevStart && d < prevEnd && c.account_status === 'active'
            }).length
            const customersGrowth = prevCustomers > 0 ? ((recentCustomers - prevCustomers) / prevCustomers * 100) : 0

            setStats({ todaySales, salesGrowth, totalOrders: totalOrdersCount, ordersGrowth, activeCustomers, customersGrowth })

            const recentList = (ordersData || []).slice(0, 5)
            const ordersWithItems = await Promise.all(recentList.map(async order => {
                const { data: items } = await supabase
                    .from('order_items')
                    .select('quantity, menu_items(name)')
                    .eq('order_id', order.order_id)
                return { ...order, items: items || [] }
            }))
            setRecentOrders(ordersWithItems)

        } catch (err) {
            console.error('Error fetching dashboard data:', err)
        } finally {
            // setLoading(false)
            setRefreshing(false)
        }
    }

    const chartData = useMemo(() => {
        if (!allOrders.length) return []

        if (chartPeriod === 'day') {
            const hours = Array.from({ length: 24 }, (_, i) => ({
                label: i === 0 ? '12am' : i < 12 ? `${i}am` : i === 12 ? '12pm' : `${i - 12}pm`,
                sales: 0, orders: 0
            }))
            allOrders.forEach(o => {
                const d = new Date(o.created_at)
                if (d >= dateStart && d <= dateEnd) {
                    hours[d.getHours()].orders += 1
                    if (o.order_status === 'completed')
                        hours[d.getHours()].sales += parseFloat(o.final_amount || 0)
                }
            })
            return hours
        }

        if (chartPeriod === 'month') {
            const dayMap = {}
            allOrders.forEach(o => {
                const d = new Date(o.created_at)
                if (d < dateStart || d > dateEnd) return
                const key = d.toLocaleDateString('en-PH', { month: 'short', day: 'numeric' })
                if (!dayMap[key]) dayMap[key] = { label: key, sales: 0, orders: 0, _t: d.getTime() }
                dayMap[key].orders += 1
                if (o.order_status === 'completed') dayMap[key].sales += parseFloat(o.final_amount || 0)
            })
            return Object.values(dayMap).sort((a, b) => a._t - b._t)
        }

        if (chartPeriod === 'year') {
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
            const data = months.map(label => ({ label, sales: 0, orders: 0 }))
            allOrders.forEach(o => {
                const d = new Date(o.created_at)
                if (d >= dateStart && d <= dateEnd) {
                    data[d.getMonth()].orders += 1
                    if (o.order_status === 'completed')
                        data[d.getMonth()].sales += parseFloat(o.final_amount || 0)
                }
            })
            return data
        }

        return []
    }, [allOrders, chartPeriod, dateStart, dateEnd])

    const paymentStats = useMemo(() => {
        const paid = transactions.filter(t => ['completed', 'paid'].includes(t.status))
        if (!paid.length) return { methods: [], top: null, totalAmount: 0, totalCount: 0 }
        const map = {}
        paid.forEach(t => {
            const key = normalizePaymentMethod(t.payment_method)
            if (!map[key]) map[key] = { method: key, count: 0, amount: 0 }
            map[key].count += 1
            map[key].amount += parseFloat(t.transaction_amount || 0)
        })
        const methods = Object.values(map).sort((a, b) => b.count - a.count)
        const totalCount = methods.reduce((s, m) => s + m.count, 0)
        const totalAmount = methods.reduce((s, m) => s + m.amount, 0)
        return { methods, top: methods[0], totalAmount, totalCount }
    }, [transactions])

    const periodRangeLabel = hasCustom
        ? `${customStart} → ${customEnd}`
        : { '1d': 'Today', '7d': 'Last 7 days', '30d': 'Last 30 days' }[preset] ?? 'Custom'

    const priorPeriodLabel = useMemo(() => {
        if (hasCustom) {
            const days = Math.round((dateEnd - dateStart) / 86400000)
            return `vs prev ${days} days`
        }
        switch (preset) {
            case '1d': return 'vs yesterday'
            case '7d': return 'vs prev 7 days'
            case '30d': return 'vs prev 30 days'
            default: return 'vs prior period'
        }
    }, [preset, hasCustom, dateStart, dateEnd])

    // if (loading) {
    //     return (
    //         <div className="flex-1 overflow-y-auto bg-gray-50">
    //             <div className="flex items-center justify-center h-screen">
    //                 <div className="text-center">
    //                     <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#A0826D] mx-auto mb-4"></div>
    //                     <p className="text-gray-600">Loading dashboard...</p>
    //                 </div>
    //             </div>
    //         </div>
    //     )
    // }



    return (
        <div className="flex-1 overflow-y-auto bg-gray-50">
            <Header
                title="Dashboard Overview"
                subtitle="Overview and analytics for your coffee shop operations"
                onGoToProfile={onGoToProfile}
                setActiveMenu={setActiveMenu}
            />

            <div className="p-8">
                <div className="sticky top-0 z-30 bg-gray-50/95 backdrop-blur-sm -mx-8 px-8 py-3 border-b border-gray-100 flex items-start justify-between flex-wrap gap-4 mb-6">
                    {/* Branch selector */}
                    <div className="flex items-center gap-3">
                        <label className="text-sm font-medium text-gray-600">Branch:</label>
                        <div className="relative" ref={branchDropdownRef}>
                            <button
                                type="button"
                                onClick={() => setBranchDropdownOpen(o => !o)}
                                className="flex items-center justify-between gap-3 px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#A0826D] bg-white min-w-[220px]"
                            >
                                <span className="flex items-center gap-2 truncate">
                                    {selectedBranch === 'all' ? (
                                        'All Branches'
                                    ) : (() => {
                                        const b = branches.find(b => b.branch_id === selectedBranch)
                                        return b ? (
                                            <>
                                                <span className="truncate">{stripBranchPrefix(b.name)}</span>
                                                {b.status && (
                                                    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap ${getBranchStatusColor(b.status)}`}>
                                                        {b.status.charAt(0).toUpperCase() + b.status.slice(1)}
                                                    </span>
                                                )}
                                            </>
                                        ) : 'Select branch'
                                    })()}
                                </span>
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" className="text-gray-400 flex-shrink-0">
                                    <path fill="currentColor" d="M7 10l5 5l5-5z" />
                                </svg>
                            </button>

                            {branchDropdownOpen && (
                                <div className="absolute z-20 mt-1 w-full min-w-[240px] bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto">
                                    <button
                                        type="button"
                                        onClick={() => { setSelectedBranch('all'); setBranchDropdownOpen(false) }}
                                        className={`w-full flex items-center px-4 py-2 text-sm text-left hover:bg-gray-50 ${selectedBranch === 'all' ? 'bg-orange-50 text-[#A0826D] font-medium' : 'text-gray-700'}`}
                                    >
                                        All Branches
                                    </button>
                                    {branches.map(b => (
                                        <button
                                            key={b.branch_id}
                                            type="button"
                                            onClick={() => { setSelectedBranch(b.branch_id); setBranchDropdownOpen(false) }}
                                            className={`w-full flex items-center justify-between gap-2 px-4 py-2 text-sm text-left hover:bg-gray-50 ${selectedBranch === b.branch_id ? 'bg-orange-50 text-[#A0826D] font-medium' : 'text-gray-700'}`}
                                        >
                                            <span className="truncate">{stripBranchPrefix(b.name)}</span>
                                            {b.status && (
                                                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap ${getBranchStatusColor(b.status)}`}>
                                                    {b.status.charAt(0).toUpperCase() + b.status.slice(1)}
                                                </span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Date range selector */}
                    <DateRangeBar
                        preset={preset} setPreset={setPreset}
                        customStart={customStart} setCustomStart={setCustomStart}
                        customEnd={customEnd} setCustomEnd={setCustomEnd}
                    />
                </div>

                {/* Active filter badge */}
                <div className="mb-5">
                    <span className="inline-flex items-center gap-1.5 text-xs text-[#A0826D] bg-orange-50 border border-orange-100 px-3 py-1 rounded-full">
                        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                        {periodRangeLabel}
                        {selectedBranch !== 'all' && ` · ${stripBranchPrefix(branches.find(b => b.branch_id === selectedBranch)?.name ?? '')}`}
                    </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                    {/* Today's Sales */}
                    <div
                        className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 cursor-pointer hover:shadow-md transition-shadow"
                        onClick={() => goToFilteredMenu('Business Analytics')}
                    >
                        <div className="flex items-start justify-between mb-3">
                            <div className="w-11 h-11 bg-green-50 rounded-xl flex items-center justify-center">
                                <span className="text-green-600 font-bold text-xl leading-none">₱</span>
                            </div>
                            {refreshing ? (
                                <SkeletonBlock className="w-12 h-5" />
                            ) : (
                                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${stats.salesGrowth >= 0 ? 'text-green-600 bg-green-50' : 'text-red-600 bg-red-50'}`}>
                                    {stats.salesGrowth >= 0 ? '+' : ''}{stats.salesGrowth.toFixed(1)}%
                                </span>
                            )}
                        </div>
                        <p className="text-sm text-gray-500 mb-1">Total Sales</p>
                        {refreshing ? (
                            <SkeletonBlock className="w-32 h-7 mb-1" />
                        ) : (
                            <h2 className="text-2xl font-bold text-gray-800">{formatCurrency(stats.todaySales)}</h2>
                        )}
                        <p className="text-xs text-gray-400 mt-1">{priorPeriodLabel}</p>
                    </div>

                    {/* Total Orders */}
                    <div
                        className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 cursor-pointer hover:shadow-md transition-shadow"
                        onClick={() => goToFilteredMenu('Order Management')}
                    >
                        <div className="flex items-start justify-between mb-3">
                            <div className="w-11 h-11 bg-blue-50 rounded-xl flex items-center justify-center">
                                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" className="text-blue-600">
                                    <path fill="currentColor" d="M7 18c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2s-.9-2-2-2M1 2v2h2l3.6 7.59l-1.35 2.45c-.16.28-.25.61-.25.96c0 1.1.9 2 2 2h12v-2H7.42c-.14 0-.25-.11-.25-.25l.03-.12l.9-1.63h7.45c.75 0 1.41-.41 1.75-1.03l3.58-6.49A1.003 1.003 0 0 0 20 4H5.21l-.94-2zm16 16c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2s2-.9 2-2s-.9-2-2-2" />
                                </svg>
                            </div>
                            {refreshing ? (
                                <SkeletonBlock className="w-12 h-5" />
                            ) : (
                                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${stats.ordersGrowth >= 0 ? 'text-green-600 bg-green-50' : 'text-red-600 bg-red-50'}`}>
                                    {stats.ordersGrowth >= 0 ? '+' : ''}{stats.ordersGrowth.toFixed(1)}%
                                </span>
                            )}
                        </div>
                        <p className="text-sm text-gray-500 mb-1">Total of Completed Orders</p>
                        {refreshing ? (
                            <SkeletonBlock className="w-32 h-7 mb-1" />
                        ) : (
                            <h2 className="text-2xl font-bold text-gray-800">{stats.totalOrders}</h2>
                        )}
                        <p className="text-xs text-gray-400 mt-1">{priorPeriodLabel}</p>
                    </div>

                    {/* Active Customers */}
                    <div
                        className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 cursor-pointer hover:shadow-md transition-shadow"
                        onClick={() => goToFilteredMenu('Customer Insight')}
                    >
                        <div className="flex items-start justify-between mb-3">
                            <div className="w-11 h-11 bg-purple-50 rounded-xl flex items-center justify-center">
                                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" className="text-purple-600">
                                    <path fill="currentColor" d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5s-3 1.34-3 3s1.34 3 3 3m-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5S5 6.34 5 8s1.34 3 3 3m0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5m8 0c-.29 0-.62.02-.97.05c1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5" />
                                </svg>
                            </div>
                            {refreshing ? (
                                <SkeletonBlock className="w-12 h-5" />
                            ) : (
                                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${stats.customersGrowth >= 0 ? 'text-green-600 bg-green-50' : 'text-red-600 bg-red-50'}`}>
                                    {stats.customersGrowth >= 0 ? '+' : ''}{stats.customersGrowth.toFixed(1)}%
                                </span>
                            )}
                        </div>
                        <p className="text-sm text-gray-500 mb-1">Active Customers</p>
                        {refreshing ? (
                            <SkeletonBlock className="w-32 h-7 mb-1" />
                        ) : (
                            <h2 className="text-2xl font-bold text-gray-800">{stats.activeCustomers}</h2>
                        )}
                        <p className="text-xs text-gray-400 mt-1">{priorPeriodLabel}</p>
                    </div>

                </div>

                {/* Sales Chart + Top Selling */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8 items-stretch">

                    {/* Sales Chart */}
                    <div className="col-span-2 bg-white rounded-xl shadow-sm border border-gray-100 flex flex-col h-full">
                        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
                            <div>
                                <h2 className="text-lg font-bold text-gray-800">Sales Overview</h2>
                                <p className="text-sm text-gray-500 mt-0.5">
                                    {selectedBranch === 'all' ? 'All branches' : stripBranchPrefix(branches.find(b => b.branch_id === selectedBranch)?.name || '')}
                                    {' · '}{periodRangeLabel}
                                </p>
                            </div>
                            <div className="flex items-center bg-gray-100 rounded-lg p-1 gap-1">
                                {['day', 'month'].map(p => (
                                    <button
                                        key={p}
                                        onClick={() => setChartPeriod(p)}
                                        className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                                            chartPeriod === p ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                                        }`}
                                    >
                                        {p === 'day' ? 'By Hour' : 'By Day'}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="p-6 flex flex-col flex-1">
                            {refreshing ? (
                                <SkeletonBlock className="w-full" style={{ minHeight: 400 }} />
                            ) : chartData.length === 0 ? (
                                <div className="flex items-center justify-center h-64 text-gray-400 text-sm">
                                    No sales data for this period
                                </div>
                            ) : (
                                <div className="w-full" style={{ minHeight: 400 }}>
                                    <ResponsiveContainer debounce={200} width="100%" height="100%">
                                        <AreaChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                                            <defs>
                                                <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#A0826D" stopOpacity={0.2} />
                                                    <stop offset="95%" stopColor="#A0826D" stopOpacity={0} />
                                                </linearGradient>
                                                <linearGradient id="ordersGradient" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.15} />
                                                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                                            <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#9ca3af' }} axisLine={false} tickLine={false}
                                                interval={chartPeriod === 'day' ? 2 : chartPeriod === 'month' ? 4 : 0} />
                                            <YAxis yAxisId="sales" orientation="left" tick={{ fontSize: 11, fill: '#9ca3af' }}
                                                axisLine={false} tickLine={false} tickFormatter={formatShortCurrency} width={60} />
                                            <YAxis yAxisId="orders" orientation="right" tick={{ fontSize: 11, fill: '#9ca3af' }}
                                                axisLine={false} tickLine={false} width={35} />
                                            <Tooltip content={<SalesTooltip />} />
                                            <Area yAxisId="sales" type="monotone" dataKey="sales" stroke="#A0826D" strokeWidth={2} fill="url(#salesGradient)" dot={false} activeDot={{ r: 4, fill: '#A0826D', strokeWidth: 0 }} />
                                            <Area yAxisId="orders" type="monotone" dataKey="orders" stroke="#3B82F6" strokeWidth={2} fill="url(#ordersGradient)" dot={false} activeDot={{ r: 4, fill: '#3B82F6', strokeWidth: 0 }} />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                            <div className="flex items-center gap-6 mt-4 text-sm text-gray-500">
                                <span className="flex items-center gap-1.5">
                                    <span className="w-3 h-3 rounded-sm" style={{ background: '#A0826D' }}></span>Sales (₱)
                                </span>
                                <span className="flex items-center gap-1.5">
                                    <span className="w-3 h-3 rounded-sm" style={{ background: '#3B82F6' }}></span>Orders (count)
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Top Selling Items */}
                    <div className="col-span-1">
                        <TopSellingItems
                            allOrders={allOrders}
                            allOrderItems={allOrderItems}
                            branches={branches}
                            compact
                            dateStart={dateStart}
                            dateEnd={dateEnd}
                            refreshing={refreshing}
                        />
                    </div>
                </div>

                {/* Payment Methods */}
                <div className="mb-8">
                    <PaymentMethodsBreakdown
                        methods={paymentStats.methods}
                        top={paymentStats.top}
                        totalAmount={paymentStats.totalAmount}
                        totalCount={paymentStats.totalCount}
                        refreshing={refreshing}
                        onViewAnalytics={() => setActiveMenu('Business Analytics')}
                    />
                </div>

                {/* Recent Orders  */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100">
                    <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
                        <div>
                            <h2 className="text-lg font-bold text-gray-800">Recent Orders</h2>
                            <p className="text-sm text-gray-500 mt-0.5">Latest 5 orders in selected period</p>
                        </div>
                        <span className="text-xs text-[#A0826D] font-medium bg-orange-50 px-3 py-1 rounded-full">
                            {recentOrders.length} shown
                        </span>
                    </div>
                    <div className="divide-y divide-gray-50">
                        {refreshing ? (
                            Array.from({ length: 5 }).map((_, i) => (
                                <div key={i} className="px-6 py-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1 space-y-2">
                                            <div className="h-4 bg-gray-200 rounded animate-pulse w-1/4" />
                                            <div className="h-3 bg-gray-200 rounded animate-pulse w-1/2" />
                                        </div>
                                        <div className="text-right ml-4 space-y-2">
                                            <div className="h-4 bg-gray-200 rounded animate-pulse w-16 ml-auto" />
                                            <div className="h-3 bg-gray-200 rounded animate-pulse w-12 ml-auto" />
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : recentOrders.length === 0 ? (
                            <div className="px-6 py-10 text-center text-gray-400 text-sm">No recent orders</div>
                        ) : (
                            recentOrders.map(order => (
                                <div key={order.order_id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-1.5">
                                                <span className="font-semibold text-gray-800 text-sm">#{order.reference_number}</span>
                                                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(order.order_status)}`}>
                                                    {order.order_status.charAt(0).toUpperCase() + order.order_status.slice(1)}
                                                </span>
                                            </div>
                                            <p className="text-sm text-gray-500">
                                                {order.items.map(item => `${item.quantity}x ${item.menu_items?.name || 'Unknown'}`).join(', ')}
                                            </p>
                                        </div>
                                        <div className="text-right ml-4">
                                            <p className="font-bold text-gray-800 text-sm">{formatCurrency(order.final_amount)}</p>
                                            <p className="text-xs text-gray-400 mt-0.5">{formatTimeAgo(order.created_at)}</p>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>

            </div>
        </div>
    )
}